import * as React from "react";
import CircularProgress from "@mui/material/CircularProgress";

import { resolveInject } from "app/di";
import {
    ISparqlExecutor,
    SparqlQueryParameters
} from "app/services/infrastructure/sparql-executor";
import {
    getRecordValue,
    SparqlRecord,
    SparqlResultParser
} from "app/services/infrastructure/sparql-result-parser";
import { ErrorMessage } from "app/ui/_generic/error-message";

import "./sparql-listing.less";
import { SpecificationDetails } from "../sections/specification-details";
import { Box } from "@mui/material";

export interface ISparqlListingState {
    isLoading: boolean;
    data: any[];
    error?: Error;
}

export interface ISparqlListingProps {
    query: string;
    queryParameters: SparqlQueryParameters;
    onLoad?: (entryCount: number) => void;
}

export class SparqlListingSpecifications extends React.Component<
    ISparqlListingProps,
    ISparqlListingState
> {
    private sparqlExecutor = resolveInject(ISparqlExecutor);

    constructor(props: ISparqlListingProps) {
        super(props);
        this.state = {
            isLoading: true,
            data: []
        };
    }

    async componentDidMount() {
        await this.loadData(this.props);
    }

    componentDidUpdate(prevProps: ISparqlListingProps) {
        if (
            prevProps.query !== this.props.query ||
            JSON.stringify(prevProps.queryParameters) !== JSON.stringify(this.props.queryParameters)
        ) {
            this.loadData(this.props);
        }
    }

    async loadData(props: ISparqlListingProps): Promise<void> {
        this.setState({ isLoading: true, error: undefined });
        try {
            const result = await this.sparqlExecutor.execute(props.query, props.queryParameters);
            const records = SparqlResultParser.parse(result);
            this.setState({ isLoading: false, data: records });

            if (this.props.onLoad) {
                this.props.onLoad(this.isEmpty() ? 0 : records.length);
            }
        } catch (ex) {
            const error = ex instanceof Error ? ex : new Error(String(ex));
            this.setState({ isLoading: false, error });
        }
    }

    isEmpty(): boolean {
        const records = this.state.data;
        return !(records && records.length > 0 && this.hasEntry(records[0]));
    }

    hasEntry(record: SparqlRecord): boolean {
        if (!record) {
            return false;
        }
        const text = getRecordValue(record, "entry_text");
        const iri = getRecordValue(record, "entry_iri");
        if ((text && text.length > 0) || (iri && iri.length > 0)) {
            return true;
        }
        return false;
    }

    render() {
        if (this.state.error) {
            return <ErrorMessage error={this.state.error} />;
        } else if (this.state.isLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={500}>
                    <CircularProgress size={48} color="primary" />
                </Box>
            );
        } else if (this.isEmpty()) {
            return <div />;
        } else {
            return this.state.data.map((s) => (
                <SpecificationDetails key={s["entry_iri"].value} specification={s} />
            ));
        }
    }
}
